// src/bankday.ts
import { UTCDate } from "@date-fns/utc";
var DAY = 24 * 60 * 60 * 1e3;
var offsetBy = (date, days) => {
  let newDay;
  if (date instanceof UTCDate) {
    newDay = new UTCDate();
  } else {
    newDay = /* @__PURE__ */ new Date();
  }
  newDay.setTime(date.getTime() + days * DAY);
  return newDay;
};
var isSame = (d1, d2) => {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
};
var getEasterDay = (year, isUTCDate) => {
  const M = 24;
  const N = 5;
  const a = year % 19;
  const b = year % 4;
  const c = year % 7;
  const d = (19 * a + M) % 30;
  const e = (2 * b + 4 * c + 6 * d + N) % 7;
  const dayMar = 22 + d + e;
  const dayApr = d + e - 9;
  let day;
  let month;
  if (dayApr <= 0) {
    day = dayMar;
  }
  if (dayMar > 31) {
    day = dayApr;
    if (day === 26) {
      day = 19;
    }
    if (day === 25 && d === 28 && a > 10) {
      day = 18;
    }
    month = 4;
  } else {
    day = dayMar;
    month = 3;
  }
  const isoString = `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  if (isUTCDate) {
    return new UTCDate(isoString);
  }
  return new Date(isoString);
};
var isEasterHoliday = (date) => {
  const isUTCDate = date instanceof UTCDate;
  const easterSunday = getEasterDay(date.getFullYear(), isUTCDate);
  const maundyThursday = offsetBy(easterSunday, -3);
  const goodFriday = offsetBy(easterSunday, -2);
  const easterMonday = offsetBy(easterSunday, 1);
  const ascensionDay = offsetBy(easterSunday, 39);
  const pentecostDay = offsetBy(easterSunday, 49);
  const whitMonday = offsetBy(easterSunday, 50);
  return isSame(date, easterSunday) || isSame(date, maundyThursday) || isSame(date, goodFriday) || isSame(date, easterMonday) || isSame(date, ascensionDay) || isSame(date, pentecostDay) || isSame(date, whitMonday);
};
var isChristmasHoliday = (date) => {
  return date.getMonth() === 11 && (date.getDate() === 24 && date.getFullYear() > 1999 || date.getDate() === 25 || date.getDate() === 26);
};
var isNewYears = (date) => {
  return date.getMonth() === 0 && date.getDate() === 1;
};
var isOldYears = (date) => {
  return date.getMonth() === 11 && date.getDate() === 31 && (date.getFullYear() > 2024 || date.getUTCHours() >= 12);
};
var isWeekend = (date) => {
  return date.getDay() === 0 || date.getDay() === 6;
};
var isIcelandicFirstDayOfSummer = (date) => {
  return date.getMonth() === 3 && date.getDate() > 18 && date.getDate() < 26 && date.getDay() === 4;
};
var isIcelandicTradesPeopleHoliday = (date) => {
  return date.getMonth() === 7 && date.getDate() < 8 && date.getDay() === 1;
};
var isIcelandicNationalHoliday = (date) => {
  return date.getMonth() === 5 && date.getDate() === 17;
};
var isInternationalLabourDay = (date) => {
  return date.getMonth() === 4 && date.getDate() === 1;
};
var isY2KDay = (date) => {
  return date.getFullYear() === 1999 && date.getMonth() === 11 && date.getDate() === 31;
};
var isBankDay = (date) => !(isEasterHoliday(date) || isChristmasHoliday(date) || isNewYears(date) || isOldYears(date) || isWeekend(date) || isIcelandicFirstDayOfSummer(date) || isIcelandicTradesPeopleHoliday(date) || isIcelandicNationalHoliday(date) || isInternationalLabourDay(date) || isY2KDay(date));
var nextBankDay = (date) => {
  let bankDay = date;
  bankDay = offsetBy(bankDay, 1);
  while (!isBankDay(bankDay)) {
    bankDay = offsetBy(bankDay, 1);
  }
  return bankDay;
};
var prevBankDay = (date) => {
  let bankDay = date;
  bankDay = offsetBy(bankDay, -1);
  while (!isBankDay(bankDay)) {
    bankDay = offsetBy(bankDay, -1);
  }
  return bankDay;
};

// src/calendarDate.ts
import { UTCDate as UTCDate2 } from "@date-fns/utc";
import {
  addBusinessDays,
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  isWithinInterval
} from "date-fns";
var DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
var CalendarDate = class _CalendarDate {
  /**
   * Year, min 0 max 9999
   */
  year;
  /**
   * Month of the year starting from 0 (index-based)
   */
  month;
  /**
   * Day of the month starting from 1
   */
  day;
  /**
   * Seconds passed since the unix epoch. Always calculated with a time and timezone set to T00:00:00.00Z.
   */
  unixTimestampInSeconds;
  isCalendarDateInstance = true;
  constructor(yearOrIsoString, month, day) {
    if (typeof yearOrIsoString === "string" || typeof yearOrIsoString === "undefined") {
      let yearString;
      if (!yearOrIsoString) {
        yearString = (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
      } else {
        if (!/^\d{4}-[01]\d-[0-3]\d$/.test(yearOrIsoString)) {
          throw new Error(
            `CalendarDate Validation Error: '${yearOrIsoString}' is not a valid ISO Date string.`
          );
        }
        yearString = yearOrIsoString;
      }
      const [year, month2, day2] = yearString.split("-").map((i) => parseInt(i));
      this.year = year;
      this.month = month2 - 1;
      this.day = day2;
    } else {
      if (month === void 0 || !day) {
        throw new Error("Invalid arguments! either Month or Day is missing");
      }
      this.year = yearOrIsoString;
      this.month = month;
      this.day = day;
    }
    if (this.year < 0 || this.year > 9999 || isNaN(this.year)) {
      throw new Error(
        `CalendarDate Validation Error: Input year '${this.year}' is not valid. Year must be a number between 0 and 9999.`
      );
    }
    if (this.month < 0 || this.month > 11 || isNaN(this.month)) {
      throw new Error(
        `CalendarDate Validation Error: Input month '${this.month}' is not valid.
If using numeric arguments, month must be between 0 and 11 (index-based).
If using an ISO string, month must be between 01 and 12.`
      );
    }
    if (this.day < 1 || isNaN(this.day)) {
      throw new Error(
        `CalendarDate Validation Error: Input day '${this.day}' is not valid. Day must be a number greater than 0.`
      );
    }
    const maxDayOfMonth = _CalendarDate.getMaxDayOfMonth(this.year, this.month);
    if (this.day > maxDayOfMonth) {
      throw new Error(
        `CalendarDate Validation Error: Input date '${this.year}-${this.month}-${this.day}' is not a valid calendar date.`
      );
    }
    const date = new Date(this.toDateTimeISOString());
    this.unixTimestampInSeconds = date.getTime() / 1e3;
    Object.freeze(this);
  }
  /**
   * Returns a new CalendarDate with the specified date parts.
   * If a part is not specified, it will default to the current value of the instance.
   * @param dateParts - An object containing the year, month, and/or day to set.
   */
  new(dateParts) {
    const { year, month, day } = dateParts;
    return new _CalendarDate(
      year ?? this.year,
      month ?? this.month,
      day ?? this.day
    );
  }
  /**
   * Same as toDateISOString
   * */
  toString() {
    return this.toDateISOString();
  }
  toDateTimeISOString() {
    return `${this.toString()}T00:00:00.000Z`;
  }
  isBankDay() {
    return isBankDay(this.toDateUTC());
  }
  /**
   * Same as toString
   * */
  toDateISOString() {
    const adjustMonth = this.month + 1;
    return `${this.year}-${adjustMonth.toString().padStart(2, "0")}-${this.day.toString().padStart(2, "0")}`;
  }
  /**
   * Returns a new CalendarDate with the specified amount of months added.
   *
   * @param amount
   * @param enforceEndOfMonth If set to true the addition will never cause an overflow to the next month.
   */
  addMonths(amount) {
    return _CalendarDate.fromDateUTC(addMonths(this.toDateUTC(), amount));
  }
  /**
   * Returns a new CalendarDate with the specified amount of months subtracted.
   * @param amount
   * @param enforceEndOfMonth If set to true the subtraction will never cause an overflow to the previous month.
   * @returns
   */
  subMonths(amount) {
    return this.addMonths(-amount);
  }
  /**
   * Returns a new CalendarDate with the specified amount of years added.
   * @param amount
   * @param enforceEndOfMonth If set to true the addition will never cause an overflow to the month of the added year.
   * @returns
   */
  addYears(amount) {
    return _CalendarDate.fromDateUTC(addYears(this.toDateUTC(), amount));
  }
  /**
   * Returns a new CalendarDate with the specified amount of years subtracted.
   * @param amount
   * @param enforceEndOfMonth If set to true the subtraction will never cause an overflow to the month of the subtracted year.
   * @returns
   */
  subYears(amount) {
    return this.addYears(-amount);
  }
  /**
   * Returns a new CalendarDate with the specified amount of days added.
   * @param amount
   */
  addDays(amount) {
    return _CalendarDate.fromDateUTC(addDays(this.toDateUTC(), amount));
  }
  /**
   * Returns a new CalendarDate with the specified amount of days removed.
   * @param amount
   */
  subDays(amount) {
    return this.addDays(-amount);
  }
  /**
   * Returns a new CalendarDate with the specified amount of business days added (Does not include holidays).
   * @param amount
   */
  addBusinessDays(amount) {
    return _CalendarDate.fromDateUTC(addBusinessDays(this.toDateUTC(), amount));
  }
  /**
   * Returns a new CalendarDate with the specified amount of business days removed (Does not include holidays).
   * @param amount
   */
  subBusinessDays(amount) {
    return this.addBusinessDays(-amount);
  }
  /**
   * Returns a new CalendarDate representing the next open bank date.
   */
  nextBankDay() {
    let date = this.addDays(1);
    while (!date.isBankDay()) {
      date = date.addDays(1);
    }
    return date;
  }
  /**
   * Returns a new CalendarDate representing the previous open bank date.
   */
  previousBankDay() {
    let date = this.subDays(1);
    while (!date.isBankDay()) {
      date = date.subDays(1);
    }
    return date;
  }
  /**
   * Returns the number of full years between this date and the other date.
   * A full year is only counted if the difference is at least 1 year and the same month and day or later.
   * Examples:
   * - Comparing 19. March 2025 [present] to 20. March 2026 [future] will return 1
   * - Comparing 19. March 2025 [present] to 18. March 2024 [past] will also return 1
   * @param otherDate CalendarDate to compare with
   * @returns number of full years between the two dates
   */
  differenceInYears(otherDate) {
    const earlierDate = this.isBefore(otherDate) ? this : otherDate;
    const laterDate = this.isAfter(otherDate) ? this : otherDate;
    return differenceInYears(laterDate.toDateUTC(), earlierDate.toDateUTC());
  }
  /**
   * Returns the number of full months between this date and the other date.
   * A full month is only counted if the difference is at least 1 month and the same day or later.
   * Examples:
   * - Comparing 19. March 2025 [present] to 20. March 2026 [future] will return 12
   * - Comparing 19. March 2025 [present] to 18. March 2024 [past] will also return 12
   * @param otherDate CalendarDate to compare with
   * @returns number of full months between the two dates
   */
  differenceInMonths(otherDate) {
    const earlierDate = this.isBefore(otherDate) ? this : otherDate;
    const laterDate = this.isAfter(otherDate) ? this : otherDate;
    return differenceInMonths(laterDate.toDateUTC(), earlierDate.toDateUTC());
  }
  /**
   * Returns the number of full days between this date and the other date.
   * A full day is only counted if the difference is at least 1 day.
   * Examples:
   * - Comparing 19. March 2025 [present] to 20. March 2026 [future] will return 366
   * - Comparing 19. March 2025 [present] to 18. March 2024 [past] will return 366
   * @param otherDate CalendarDate to compare with
   * @returns number of full days between the two dates
   */
  differenceInDays(otherDate) {
    const earlierDate = this.isBefore(otherDate) ? this : otherDate;
    const laterDate = this.isAfter(otherDate) ? this : otherDate;
    return differenceInDays(laterDate.toDateUTC(), earlierDate.toDateUTC());
  }
  /**
   * Returns a new CalendarDate representing the start of the month.
   */
  startOfMonth() {
    return this.new({ day: 1 });
  }
  /**
   * Returns a new CalendarDate representing the end of the month.
   */
  endOfMonth() {
    return this.new({
      day: _CalendarDate.getMaxDayOfMonth(this.year, this.month)
    });
  }
  /**
   * Returns a new CalendarDate representing the start of the year.
   */
  startOfYear() {
    return this.new({ day: 1, month: 0 });
  }
  /**
   * Returns a new CalendarDate representing the end of the year.
   */
  endOfYear() {
    return this.new({
      day: _CalendarDate.getMaxDayOfMonth(this.year, 11),
      month: 11
    });
  }
  /**
   * Checks inclusively if the date is within the specified interval.
   * @param interval Interval to check between start and end.
   * @returns true if the date is within the interval.
   */
  isWithinInterval({ start, end }) {
    const earlierDate = start.isBefore(end) ? start : end;
    const laterDate = start.isAfter(end) ? start : end;
    return isWithinInterval(this.toDateUTC(), {
      start: earlierDate.toDateUTC(),
      end: laterDate.toDateUTC()
    });
  }
  /**
   * Checks if the date is in the future.
   * @returns true if the date is in the future
   */
  isFuture() {
    const today = new _CalendarDate();
    return this > today;
  }
  /**
   * Checks if the date is in the past.
   * @returns true if the date is in the past
   */
  isPast() {
    const today = new _CalendarDate();
    return this < today;
  }
  /**
   * Checks if the date is after the other date.
   * @param other CalendarDate to compare with
   * @returns true if the date is after the other date
   */
  isAfter(other) {
    return this > other;
  }
  /**
   * Checks if the date is before the other date.
   * @param other CalendarDate to compare with
   * @returns true if the date is before the other date
   */
  isBefore(other) {
    return this < other;
  }
  /**
   * Checks if the date is in the same year as the other date.
   * @param other CalendarDate to compare with
   * @returns true if the date is in the same year as the other date
   */
  isSameYear(other) {
    return this.year === other.year;
  }
  /**
   * Checks if the current date is the first day of the month.
   */
  isFirstDayOfMonth() {
    return this.day === 1;
  }
  /**
   * Checks if the current date is the same day as the other date.
   * @param other CalendarDate to compare with
   * @returns true if the date is the same day as the other date
   */
  isEqual(other) {
    return this.unixTimestampInSeconds === other.unixTimestampInSeconds;
  }
  /**
   * Returns the day of the week for the current date.
   * The day of the week is a number between 0 and 6 where 0 is Sunday and 6 is Saturday.
   * @returns The day of the week for the current date.
   */
  getDay() {
    return this.toDateUTC().getDay();
  }
  /**
   * Return a day of the current month of the year
   */
  getDate() {
    return this.day;
  }
  /**
   * Return a day of the current month of the year
   */
  getMonth() {
    return this.month;
  }
  /**
   * Return a day of the current month of the year
   */
  getFullYear() {
    return this.year;
  }
  /**
   * Returns the ordinal day of the month.
   * Based on the locale, it will return the correct suffix.
   * @param locale - The locale to use for the suffix.
   */
  getOrdinalDay(locale = "en") {
    const suffixes = {
      is: ["."],
      en: ["th", "st", "nd", "rd"],
      pl: [""]
    };
    const suffix = suffixes[locale];
    return this.day + (suffix[this.day % 10] ?? suffix[this.day] ?? suffix[0]);
  }
  /**
   * Return total days passed in the current year
   */
  getDayOfYear() {
    let dayOfYear = this.day;
    for (let monthIdx = 0; monthIdx < this.month; monthIdx++) {
      if (monthIdx === 1 && _CalendarDate.isLeapYear(this.year)) {
        dayOfYear += 29;
        continue;
      }
      dayOfYear += DAYS_IN_MONTH[monthIdx];
    }
    return dayOfYear;
  }
  toJSON() {
    return this.toString();
  }
  toDateUTC() {
    return new UTCDate2(this.unixTimestampInSeconds * 1e3);
  }
  /**
   * Used for comparison
   * */
  valueOf() {
    return this.unixTimestampInSeconds;
  }
  /**
   * Creates a new CalendarDate from a Date object in UTC.
   * @param {Date | UTCDate} date - The date to convert. Can be a standard Date or a UTCDate.
   * @returns {CalendarDate} A new CalendarDate instance representing the same date.
   */
  static fromDateUTC(date) {
    return new _CalendarDate(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );
  }
  /**
   * Creates a new CalendarDate from an ISO date string.
   * The ISO string should be in the format 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm:ss.sssZ'.
   * Only the date part is used; the time part (if present) is ignored.
   * The month in the ISO string is not index-based (01 = January, 12 = December) and will be converted internally.
   * @param {string} isoString - The ISO date string to convert.
   * @returns {CalendarDate} A new CalendarDate instance representing the specified date.
   * @example
   * CalendarDate.fromISODateTimeString('2025-04-26'); // April 26, 2025
   * CalendarDate.fromISODateTimeString('2025-04-26T15:30:00.000Z'); // April 26, 2025
   */
  static fromISODateTimeString(isoString) {
    const dateString = isoString.split("T")[0];
    return new _CalendarDate(dateString);
  }
  /**
   * Creates a new CalendarDate from a UTC Date object in local time.
   * @returns {CalendarDate} A new CalendarDate instance representing the current date in UTC.
   */
  static nowUTC() {
    return _CalendarDate.fromDateUTC(new UTCDate2());
  }
  /**
   * Determines if the given year is a leap year.
   * A leap year is divisible by 4, but not by 100 unless also divisible by 400.
   * @param {number} year - The year to check.
   * @returns {boolean} True if the year is a leap year, false otherwise.
   */
  static isLeapYear(year) {
    return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
  }
  /**
   * Returns the maximum number of days in a given month of a given year.
   * Takes into account leap years for February.
   * @param {number} year - The year to check.
   * @param {number} month - The month to check (index-based: 0 = January, 11 = December).
   * @returns {number} The maximum number of days in the specified month of the specified year.
   */
  static getMaxDayOfMonth(year, month) {
    return month === 1 && _CalendarDate.isLeapYear(year) ? 29 : DAYS_IN_MONTH[month];
  }
  /**
   * Checks if the given object is an instance of CalendarDate.
   * @param {unknown} obj - The object to check.
   * @returns {boolean} True if the object is an instance of CalendarDate, false otherwise.
   */
  static isCalendarDate(obj) {
    return typeof obj === "object" && obj !== null && "isCalendarDateInstance" in obj && obj.isCalendarDateInstance === true;
  }
};
var calendarDate_default = CalendarDate;

// src/date.ts
var monthNamesSettings = {
  is: {
    short: [
      "Jan.",
      "Feb.",
      "Mars",
      "Apr\xEDl",
      "Ma\xED",
      "J\xFAn\xED",
      "J\xFAl\xED",
      "\xC1g\xFAst",
      "Sept.",
      "Okt.",
      "N\xF3v.",
      "Des."
    ],
    long: [
      "Jan\xFAar",
      "Febr\xFAar",
      "Mars",
      "Apr\xEDl",
      "Ma\xED",
      "J\xFAn\xED",
      "J\xFAl\xED",
      "\xC1g\xFAst",
      "September",
      "Okt\xF3ber",
      "N\xF3vember",
      "Desember"
    ],
    weekdays: [
      "Sunnudagur",
      "M\xE1nudagur",
      "\xDEri\xF0judagur",
      "Mi\xF0vikudagur",
      "Fimmtudagur",
      "F\xF6studagur",
      "Laugardagur"
    ],
    weekdaysShort: ["Sun.", "M\xE1n.", "\xDEri.", "Mi\xF0.", "Fim.", "F\xF6s.", "Lau."]
  },
  en: {
    short: [
      "Jan.",
      "Feb.",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "Sept.",
      "Oct.",
      "Nov.",
      "Dec."
    ],
    long: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ],
    weekdays: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday"
    ],
    weekdaysShort: ["Sun.", "Mon.", "Tues.", "Wed.", "Thur.", "Fri.", "Sat."]
  },
  pl: {
    short: [
      "Sty.",
      "Lut.",
      "Mar.",
      "Kwi.",
      "Maj.",
      "Cze.",
      "Lip.",
      "Sie.",
      "Wrz.",
      "Pa\u017A.",
      "Lis.",
      "Gru."
    ],
    long: [
      "w(e) STY",
      "w(e) LUT",
      "w(e) MAR",
      "w(e) KWI",
      "w(e) MAJ",
      "w(e) CZE",
      "w(e) LIP",
      "w(e) SIE",
      "w(e) WRZ",
      "w(e) PA\u0179",
      "w(e) LIS",
      "w(e) GRU"
    ],
    weekdays: [
      "Niedziela",
      "Poniedzia\u0142ek",
      "Wtorek",
      "\u015Aroda",
      "Czwartek",
      "Pi\u0105tek",
      "Sobota"
    ],
    weekdaysShort: ["Niedz.", "Pon.", "Wt.", "\u015Ar.", "Czw.", "Pt.", "Sob."]
  }
};
var monthWeekdayCasing = (monthOrWeekday, locale, firstLetterCapital) => {
  const toLowerCase = locale !== "en" && (!firstLetterCapital || firstLetterCapital !== true);
  return toLowerCase ? monthOrWeekday.toLowerCase() : monthOrWeekday;
};
var formatDate = (locale, date) => {
  const day = `0${date.getDate()}`.slice(-2);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const year = date.getFullYear();
  switch (locale) {
    case "en":
      return `${day}/${month}/${year}`;
    case "pl":
    default:
      return `${day}.${month}.${year}`;
  }
};
var formatTime = (locale, time) => {
  let mins = `${time.getMinutes()}`;
  if (mins.length === 1) {
    mins = `0${mins}`;
  }
  switch (locale) {
    case "en":
      return time.getHours() / 12 > 1 ? `${time.getHours() - 12}:${mins} PM` : `${time.getHours()}:${mins} AM`;
    case "pl":
    default:
      return `${time.getHours()}:${mins}`;
  }
};
var formatWeekday = (locale, date, firstLetterCapital) => monthWeekdayCasing(
  monthNamesSettings[locale].weekdays[date.getDay()],
  locale,
  firstLetterCapital
);
var formatWeekdayShort = (locale, date, firstLetterCapital) => monthWeekdayCasing(
  monthNamesSettings[locale].weekdaysShort[date.getDay()],
  locale,
  firstLetterCapital
);
var formatDateTime = (locale, datetime) => `${formatDate(locale, datetime)} ${formatTime(locale, datetime)}`;
var formatShortMonthFromIdx = (locale, idx, firstLetterCapital) => monthWeekdayCasing(
  monthNamesSettings[locale].short[idx],
  locale,
  firstLetterCapital
);
var formatLongMonthFromIdx = (locale, idx, firstLetterCapital) => monthWeekdayCasing(
  monthNamesSettings[locale].long[idx],
  locale,
  firstLetterCapital
);
var formatShortMonth = (locale, date, firstLetterCapital) => formatShortMonthFromIdx(locale, date.getMonth(), firstLetterCapital);
var formatLongMonth = (locale, date, firstLetterCapital) => formatLongMonthFromIdx(locale, date.getMonth(), firstLetterCapital);
var formatMonthYear = (locale, date, firstLetterCapital) => `${formatShortMonth(locale, date, firstLetterCapital)} ${date.getFullYear()}`;
var formatMonthYearLong = (locale, date, firstLetterCapital) => `${formatLongMonth(locale, date, firstLetterCapital)} ${date.getFullYear()}`;
var formatDayMonthLong = (locale, date) => {
  const month = formatLongMonth(locale, date, false);
  switch (locale) {
    case "en":
    case "pl":
      return `${date.getDate()} ${month}`;
    default:
      return `${date.getDate()}. ${month}`;
  }
};
var formatDateLong = (locale, date) => {
  const month = formatLongMonth(locale, date, false);
  switch (locale) {
    case "en":
    case "pl":
      return `${date.getDate()} ${month} ${date.getFullYear()}`;
    default:
      return `${date.getDate()}. ${month} ${date.getFullYear()}`;
  }
};
var formatDateShort = (locale, date) => {
  const month = formatShortMonth(locale, date, false);
  switch (locale) {
    case "en":
    case "pl":
      return `${date.getDate()} ${month} ${date.getFullYear()}`;
    default:
      return `${date.getDate()}. ${month} ${date.getFullYear()}`;
  }
};
var formatDayMonth = (locale, date) => {
  const month = formatShortMonth(locale, date, false);
  switch (locale) {
    case "en":
    case "pl":
      return `${date.getDate()} ${month}`;
    default:
      return `${date.getDate()}. ${month}`;
  }
};

// src/formatCalendarDate.ts
var formatCalendarDate = (locale, date) => {
  const day = `0${date.getDate()}`.slice(-2);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const year = date.getFullYear();
  switch (locale) {
    case "en":
      return `${day}/${month}/${year}`;
    case "pl":
    case "is":
      return `${day}.${month}.${year}`;
  }
};
var formatCalendarDateWeekday = (locale, date, firstLetterCapital) => monthWeekdayCasing(
  monthNamesSettings[locale].weekdays[date.getDay()],
  locale,
  firstLetterCapital
);
var formatCalendarDateWeekdayShort = (locale, date, firstLetterCapital) => monthWeekdayCasing(
  monthNamesSettings[locale].weekdaysShort[date.getDay()],
  locale,
  firstLetterCapital
);
var formatCalendarDateShortMonth = (locale, date, firstLetterCapital) => formatShortMonthFromIdx(locale, date.getMonth(), firstLetterCapital);
var formatCalendarDateLongMonth = (locale, date, firstLetterCapital) => formatLongMonthFromIdx(locale, date.getMonth(), firstLetterCapital);
var formatCalendarDateMonthYear = (locale, date, firstLetterCapital) => `${formatCalendarDateShortMonth(locale, date, firstLetterCapital)} ${date.getFullYear()}`;
var formatCalendarDateMonthYearLong = (locale, date, firstLetterCapital) => `${formatCalendarDateLongMonth(locale, date, firstLetterCapital)} ${date.getFullYear()}`;
var formatCalendarDateDayMonthLong = (locale, date) => {
  const month = formatCalendarDateLongMonth(locale, date, false);
  switch (locale) {
    case "en":
    case "pl":
      return `${date.getDate()} ${month}`;
    case "is":
      return `${date.getDate()}. ${month}`;
  }
};
var formatCalendarDateLong = (locale, date) => {
  const month = formatCalendarDateLongMonth(locale, date, false);
  switch (locale) {
    case "en":
    case "pl":
      return `${date.getDate()} ${month} ${date.getFullYear()}`;
    case "is":
      return `${date.getDate()}. ${month} ${date.getFullYear()}`;
  }
};
var formatCalendarDateShort = (locale, date) => {
  const month = formatCalendarDateShortMonth(locale, date, false);
  switch (locale) {
    case "en":
    case "pl":
      return `${date.getDate()} ${month} ${date.getFullYear()}`;
    case "is":
      return `${date.getDate()}. ${month} ${date.getFullYear()}`;
  }
};
var formatCalendarDateDayMonth = (locale, date) => {
  const month = formatCalendarDateShortMonth(locale, date, false);
  switch (locale) {
    case "en":
    case "pl":
      return `${date.getDate()} ${month}`;
    case "is":
      return `${date.getDate()}. ${month}`;
  }
};

// src/resource.tsx
var getCountSpecificResourceKey = (locale, resources) => {
  const resourceObject = resources[locale];
  const undefinedCheck = (object) => {
    return object !== void 0 && object.zero !== void 0 && object.one !== void 0 && object.many !== void 0;
  };
  if (undefinedCheck(resourceObject)) {
    return resourceObject;
  }
  if (process.env.NODE_ENV === "development") {
    console.warn(
      `Missing resource count specific key '${JSON.stringify(resourceObject)}' for locale '${locale}'`
    );
  }
  if (undefinedCheck(resources.is)) {
    return resources.is;
  }
  if (undefinedCheck(resources.en)) {
    return resources.en;
  }
  return { zero: "", one: "", many: "" };
};
var getResources = (locale, resources) => {
  const resource = resources[locale];
  if (resource !== void 0) {
    return resource;
  }
  if (process.env.NODE_ENV === "development") {
    console.warn(
      `Missing resource key '${JSON.stringify(resources)}' for locale '${locale}'`
    );
  }
  if (resources.is !== void 0) {
    return resources.is;
  }
  if (resources.en !== void 0) {
    return resources.en;
  }
  return "";
};
var getCountSpecificResource = (locale, resources, count) => {
  if (isNaN(count) || count % 1 !== 0) {
    return "";
  }
  const resourceObject = getCountSpecificResourceKey(locale, resources);
  if (count === 0) {
    return resourceObject.zero.replace("{count}", count);
  }
  if (count === 1) {
    return resourceObject.one.replace("{count}", count);
  }
  if (locale === "is" && (count - 11) % 100 === 0) {
    return resourceObject.many.replace("{count}", count);
  }
  if (locale === "is" && count % 10 === 1) {
    return resourceObject.one.replace("{count}", count);
  }
  if (locale === "pl" && resourceObject.few !== void 0) {
    if ((count - 12) % 100 === 0 || (count - 13) % 100 === 0 || (count - 14) % 100 === 0) {
      return resourceObject.many.replace("{count}", count);
    }
    if (count > 1 && count < 5 || count % 10 > 1 && count % 10 < 5) {
      return resourceObject.few.replace("{count}", count);
    }
  }
  if (locale === "pl" && count % 100 === 0) {
    return resourceObject.one.replace("{count}", count);
  }
  return resourceObject.many.replace("{count}", count);
};
function r(locale, resources, count) {
  if (resources === void 0) {
    if (process.env.NODE_ENV === "development") {
      throw new Error(`Undefined resource for locale ${locale}`);
    }
    return "";
  }
  if (count === void 0) {
    return getResources(locale, resources);
  }
  if (count !== void 0) {
    return getCountSpecificResource(locale, resources, count);
  }
}
function res(locale) {
  return (resources, count) => r(locale, resources, count);
}

// src/settings.ts
var numberSettings = {
  is: {
    delimiter: ",",
    thousand: "."
  },
  en: {
    delimiter: ".",
    thousand: ","
  },
  pl: {
    delimiter: ",",
    thousand: " "
  }
};

// src/number.ts
var includeThousandSeparator = (locale, number, precision) => {
  const t = numberSettings[locale].thousand;
  const delimiter = numberSettings[locale].delimiter;
  const prefix = number < 0 ? "-" : "";
  const parts = Math.abs(number).toFixed(precision).toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
  return prefix + parts.join(delimiter);
};
var replaceAll = (s, from, to) => {
  let target = s;
  let i = target.indexOf(from);
  while (i !== -1) {
    target = target.replace(from, to);
    i = target.indexOf(from);
  }
  return target;
};
var formatInt = (locale, number, round) => {
  let n = number;
  if (round !== void 0 && !round) {
    n = Math.floor(n);
  }
  return includeThousandSeparator(locale, n, 0);
};
var formatFloat = (locale, number, prec, strip) => {
  let n = number;
  const precision = prec !== void 0 ? prec : 2;
  if (precision) {
    const p = Math.pow(10, precision);
    n = Math.round(n * p) / p;
  }
  const formatted = includeThousandSeparator(locale, n, precision);
  if (strip && precision > 0) {
    let stripped = formatted.replace(/0+$/, "").replace(/,+$/, "");
    if (stripped.endsWith(".")) {
      stripped = stripped.substr(0, stripped.length - 1);
    }
    return stripped;
  }
  return formatted;
};
var formatMoney = (locale, amount, currencyCode, suffix, customPrecision) => {
  if (amount !== null && amount !== void 0 && currencyCode && locale) {
    const precision = currencyCode === "ISK" || currencyCode === "JPY" ? 0 : 2;
    const amountFormatted = formatFloat(
      locale,
      amount,
      customPrecision !== void 0 ? customPrecision : precision
    );
    return suffix ? `${amountFormatted} ${currencyCode}` : amountFormatted;
  }
  return null;
};
var formatExchangeRate = (locale, rate, currencyCode, strip, customPrecision) => {
  let precision = 2;
  if (customPrecision !== void 0) {
    precision = customPrecision;
  } else if (currencyCode === "JPY") {
    precision = 4;
  } else if (currencyCode === "DKK" || currencyCode === "SEK" || currencyCode === "NOK" || currencyCode === "HKD" || currencyCode === "CNY" || currencyCode === "ZAR" || currencyCode === "PLN") {
    precision = 3;
  }
  return formatFloat(locale, rate, precision, strip);
};
var formatPercent = (locale, n, prec) => formatFloat(locale, n * 100, prec);
var toFloat = (locale, s) => {
  const Settings = numberSettings[locale];
  let string = replaceAll(s, Settings.thousand, "");
  const d = Settings.delimiter;
  if (d !== ".") {
    string = string.replace(d, ".");
  }
  return parseFloat(string);
};
var toInt = (locale, s, round) => {
  const shouldRound = round === void 0 ? true : round;
  let f = toFloat(locale, s);
  if (shouldRound) {
    f = Math.round(f);
  } else {
    f = Math.floor(f);
  }
  return f;
};

export {
  isBankDay,
  nextBankDay,
  prevBankDay,
  calendarDate_default,
  formatDate,
  formatTime,
  formatWeekday,
  formatWeekdayShort,
  formatDateTime,
  formatShortMonthFromIdx,
  formatLongMonthFromIdx,
  formatShortMonth,
  formatLongMonth,
  formatMonthYear,
  formatMonthYearLong,
  formatDayMonthLong,
  formatDateLong,
  formatDateShort,
  formatDayMonth,
  formatCalendarDate,
  formatCalendarDateWeekday,
  formatCalendarDateWeekdayShort,
  formatCalendarDateShortMonth,
  formatCalendarDateLongMonth,
  formatCalendarDateMonthYear,
  formatCalendarDateMonthYearLong,
  formatCalendarDateDayMonthLong,
  formatCalendarDateLong,
  formatCalendarDateShort,
  formatCalendarDateDayMonth,
  r,
  res,
  numberSettings,
  formatInt,
  formatFloat,
  formatMoney,
  formatExchangeRate,
  formatPercent,
  toFloat,
  toInt
};
//# sourceMappingURL=chunk-P5TPVON7.mjs.map